<template>
  <div>
    <v-container class="toolbar__container">
      <v-row>
        <v-col cols="12" sm="8" md="5">
          <v-autocomplete
            v-model="idEquipamento"
            :items="equipamentosHikcentral"
            :item-text="rotuloEquipamento"
            item-value="id_equipamento"
            clearable
            :label="$t('statusDispositivos.filters.equipment')"
            :placeholder="$t('statusDispositivos.messages.allEquipments')"
            persistent-placeholder
          />
        </v-col>
        <v-col cols="12" sm="4" md="2" class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn small fab color="primary darken-1" class="white--text" v-bind="attrs" v-on="on" :loading="loading" @click="popula">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('statusDispositivos.actions.search') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <merus-data-table
      :key="tableKey"
      cod-tabela="EQUIPAMENTOS__VALIDADORES_ERROS_HIKCENTRAL"
      v-model="selecionados"
      :headers="headers"
      :items="itens"
      :loading="loading"
      item-key="chave"
      :show-select="podeReaplicar"
      @update:headers="headers = $event"
    >
      <template v-slot:toolbar>
        <CadastroToolbar
          :title="$t('equipment.panel.hikcentralErrors.title')"
          :token="token"
          :items="itens"
          csv-title="validadores_erros_hikcentral"
          :csv-headers="headers"
          :pdf-title="$t('equipment.panel.hikcentralErrors.title')"
          :pdf-headers="headers"
          pdf-filename="validadores_erros_hikcentral"
          hide-search
        >
          <template v-if="podeReaplicar" v-slot:add>
            <v-tooltip top max-width="360">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-btn fab small color="primary" dark class="mb-2 mr-2"
                    :disabled="selecionados.length === 0 || reaplicando" :loading="reaplicando" @click="reaplica(selecionados)">
                    <v-icon>mdi-sync</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>{{ $t('statusDispositivos.actions.reapplySelected') }} — {{ $t('equipment.help.hikcentralReapply') }}</span>
            </v-tooltip>
          </template>
        </CadastroToolbar>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-tooltip v-if="podeReaplicar" top>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-btn icon small color="primary" :disabled="reaplicando || !item.cod_integracao_faceid || !item.cod_integracao" @click="reaplica([item])">
                <v-icon small>mdi-sync</v-icon>
              </v-btn>
            </span>
          </template>
          <span>{{ item.cod_integracao_faceid && item.cod_integracao ? $t('statusDispositivos.actions.reapply') : $t('equipment.panel.hikcentralErrors.missingCodes') }}</span>
        </v-tooltip>
      </template>
    </merus-data-table>
  </div>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import MerusDataTable from '../../Tables/MerusDataTable.vue';
import CadastroToolbar from '../../Toolbars/CadastroToolbar.vue';
import dispositivosFormatters from './dispositivosFormatters';
import recorteEquipamentos from './recorteEquipamentos';

/**
 * Erros de aplicação do nível de acesso no HikCentral, gravados pela rotina
 * (erros_envio_hikvision): pessoa que está no nível mas não chegou ao leitor. A lista vem do
 * banco; o reenvio pede ao HikCentral para reaplicar o nível às pessoas na porta do leitor, e a
 * lista só muda na próxima corrida da rotina.
 */
export default {
  name: 'ErrosHikcentral',
  components: { MerusDataTable, CadastroToolbar },
  mixins: [dispositivosFormatters, recorteEquipamentos],
  props: {
    equipamentos: { type: Array, default: () => [] },
    token: { type: String, default: null },
    podeReaplicar: { type: Boolean, default: false },
  },
  data: () => ({
    idEquipamento: null,
    loading: false,
    reaplicando: false,
    itens: [],
    selecionados: [],
    headers: [],
  }),
  computed: {
    equipamentosHikcentral() {
      return (this.equipamentos || []).filter((e) => e.suite_efetiva === 'HIKCENTRAL');
    },
    tableKey() {
      return `validadores-erros-hikcentral-${this.$i18n.locale}`;
    },
  },
  watch: {
    '$i18n.locale'() {
      this.initializeHeaders();
    },
  },
  created() {
    this.initializeHeaders();
    this.popula();
  },
  methods: {
    rotuloEquipamento(item) {
      return item.cod_veiculo ? `${item.nome_equipamento} (${item.cod_veiculo})` : item.nome_equipamento;
    },
    initializeHeaders() {
      const h = this.$t.bind(this);
      this.headers = [
        { text: h('statusDispositivos.headers.equipment'), value: 'nome_equipamento', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.vehicle'), value: 'cod_veiculo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.registrationCode'), value: 'matricula', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.personName'), value: 'nome_completo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.integrationCode'), value: 'cod_integracao_faceid', sortable: true, filtrable: true },
        { text: h('equipment.panel.hikcentralErrors.errorType'), value: 'id_tipo_erro', sortable: true, filtrable: true },
      ];
    },
    popula() {
      this.loading = true;
      this.selecionados = [];
      const filtro = this.idEquipamento != null ? { id_equipamento: this.idEquipamento } : {};
      apiController.request(apiController.endpoints.isapiGetErroshikcentral, {
        data: filtro,
        onSuccess: (response) => {
          this.itens = this.recortaDaLista(response.data || []).map((linha, i) => ({
            ...linha,
            chave: `${linha.id_equipamento}-${linha.id_pessoa}-${linha.id_tipo_erro}-${i}`,
            nome_completo: this.fmtTexto(linha.nome_completo),
          }));
        },
        onError: () => {
          this.itens = [];
        },
      })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    // Um pedido por leitor: o HikCentral reaplica o nível às pessoas na porta informada.
    async reaplica(linhas) {
      const porPorta = new Map();
      (linhas || []).forEach((l) => {
        if (!l.cod_integracao || !l.cod_integracao_faceid) return;
        if (!porPorta.has(l.cod_integracao)) porPorta.set(l.cod_integracao, new Set());
        porPorta.get(l.cod_integracao).add(l.cod_integracao_faceid);
      });
      if (porPorta.size === 0) {
        EventBus.$emit('show-error', { error: this.$t('equipment.panel.hikcentralErrors.missingCodes') });
        return;
      }
      this.reaplicando = true;
      try {
        const respostas = await Promise.all(Array.from(porPorta.entries()).map(([porta, pessoas]) =>
          apiController.request(apiController.endpoints.hikvisionReaplyAccesslevel, {
            data: { personids: Array.from(pessoas).join(','), doorindexcodes: porta },
            silent: true,
          })));
        const falhou = respostas.some((r) => r && r.data && r.data.msg === 'Erro');
        if (falhou) {
          EventBus.$emit('show-error', { error: this.$t('equipment.panel.hikcentralErrors.reapplyFailed') });
        } else {
          EventBus.$emit('show-success', { message: this.$t('equipment.panel.hikcentralErrors.reapplied') });
        }
        this.selecionados = [];
      } catch (error) {
        EventBus.$emit('show-error', { error });
      } finally {
        this.reaplicando = false;
      }
    },
  },
};
</script>
